import React, {useEffect, useState} from 'react';
import {Alert, Button, Modal, Table} from 'antd';
import {Link, useLocation, useNavigate} from '../helpers/routerCompat';
import {useTranslation} from 'react-i18next';
import api from '../api';
import useRequestRefreshScheduler from '../helpers/useRequestRefreshScheduler';

export const UploadBatchLink = ({id}) => {
    const location = useLocation();
    const {t} = useTranslation();
    const search = new URLSearchParams(location.search);
    search.set('upload_batch', id);
    return <Link to={{pathname: location.pathname, search: `?${search}`, hash: location.hash}}>
        {t('view-upload', 'View upload')}
    </Link>;
};

// A shareable, read-only batch view. It intentionally offers no upload-wide
// edit controls: several preparations can share the same Upload and folder.
export default function UploadBatchDetails({organizationId}) {
    const location = useLocation();
    const navigate = useNavigate();
    const id = new URLSearchParams(location.search).get('upload_batch');
    if (!organizationId || !/^\d+$/.test(id || '')) return null;
    const close = () => {
        const search = new URLSearchParams(location.search);
        search.delete('upload_batch');
        navigate({...location, search: search.size ? `?${search}` : ''}, {replace: true});
    };
    return <BatchContents key={`${organizationId}:${id}`} id={id} onClose={close}/>;
}

function BatchContents({id, onClose}) {
    const {t} = useTranslation();
    const [page, setPage] = useState(1);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    const scheduler = useRequestRefreshScheduler(() => api(`/api/bulk_uploads/${id}/status?include_files=true&page=${page}`), {
        delay: 0, debounce: false, deferWhenHidden: true, refreshKey: `${id}:${page}`,
        applyResult: response => { setResult({page, data: response.data}); setError(null); },
        onError: failure => setError(failure.response?.status === 404 ? 'missing' : 'failed'),
    });
    useEffect(() => {
        if (error === 'missing') return;
        scheduler.schedule();
        const interval = setInterval(() => scheduler.schedule(), 5000);
        return () => clearInterval(interval);
    }, [scheduler, page, error === 'missing']);
    const data = result?.page === page ? result.data : null;
    const progress = data?.progress;
    const states = {
        pending: t('pending-upload', 'Pending upload'), uploading: t('uploading', 'Uploading'),
        uploaded: t('waiting-for-processing', 'Waiting for processing'), processing: t('processing', 'Processing'),
        processed: t('processed', 'Processed'), processing_error: t('processing-error', 'Processing error'),
        preparation_error: t('upload-preparation-error', 'Could not prepare upload'),
        trashed: t('in-trash', 'In Trash'), removed: t('removed', 'Removed'),
    };
    return <Modal open title={t('upload-batch', 'Upload batch {{id}}', {id})} onCancel={onClose}
                  footer={<Button onClick={onClose}>{t('close', 'Close')}</Button>} width={720}>
        {error && <Alert type="warning" showIcon message={error === 'missing'
            ? t('upload-batch-unavailable', 'This upload batch was deleted or is no longer available.')
            : t('upload-batch-refresh-failed', 'Could not refresh this upload batch. Retrying…')}/>}
        {progress && <p>
            {t('upload-files-processed', '{{done}} of {{total}} files processed', {done: progress.completed, total: progress.total})}
            {` · ${t('upload-files-in-trash', '{{count}} in Trash', {count: progress.trashed || 0})}`}
            {` · ${t('upload-files-deleted', '{{count}} deleted', {count: progress.deleted || 0})}`}
        </p>}
        <Table rowKey="id" size="small" loading={!data && !error} dataSource={data?.files?.items || []}
               columns={[
                   {title: t('filename', 'Filename'), dataIndex: 'filename', render: name => <span style={{overflowWrap: 'anywhere'}}>{name}</span>},
                   {title: t('status', 'Status'), dataIndex: 'state', render: state => states[state] || state},
               ]}
               pagination={{current: page, pageSize: 50, total: result?.data?.files?.total || 0, showSizeChanger: false,
                   onChange: next => {setPage(next); setError(null);}}}/>
        {progress && data.files.total < progress.total && <p>
            {t('upload-deleted-files-not-listed', 'Permanently removed file records remain in the totals but can no longer be listed.')}
        </p>}
    </Modal>;
}
